/* eslint-disable react/jsx-no-useless-fragment */
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import { memo } from 'react';
import OutOfStock from '../OutOfStock';
import { APP_ENV, CategoryItemsProps, Template } from '@zeus/shared';
import { getScaleSize, isBetweenRange } from '../../../../../utils/utils';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import { TEMPLATE_1_MODIFIERS } from '../../../../../utils/consts';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template;
  item: Item;
  development?: boolean;
  timezone?: string;
  categoryIndex?: number;
  categoryId?: number;
  itemIndex: number;
  scale?: number;
  hasAnyModifier: boolean;
  lastItemIndex: number;
  tenantId?: number;
  fontFamily?: string;
}

const Item = ({
  template,
  item,
  development,
  timezone,
  categoryIndex,
  categoryId,
  itemIndex,
  scale = 1,
  hasAnyModifier,
  lastItemIndex,
  tenantId,
  fontFamily: fontFamilyProp,
}: ItemProps) => {
  const { normalizePixels } = useNormalizedPixels();
  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const isDescription = template?.itemDescription?.isEnabled;
  const isBrandLogo = template.brandLogo?.isEnabled && template.brandLogo?.image;

  const getBottomMargin = () => {
    if (categoryIndex === 1) {
      if (isBrandLogo) {
        if (isDescription) {
          const margin = development ? 10 : 3;
          return { marginTop: getScaleSize(normalizePixels(margin, 'height'), scale) };
        } else {
          return { marginTop: getScaleSize(normalizePixels(10, 'height'), scale) };
        }
      } else {
        if (isDescription) {
          return { marginTop: getScaleSize(normalizePixels(20, 'height'), scale) };
        } else {
          return { marginTop: getScaleSize(normalizePixels(18, 'height'), scale) };
        }
      }
    } else {
      if (isDescription) {
        return { marginTop: getScaleSize(normalizePixels(12, 'height'), scale) };
      } else {
        return { marginTop: getScaleSize(normalizePixels(16, 'height'), scale) };
      }
    }
  };

  const getMaxWidth = () => {
    if (template?.itemPrice && template.itemPrice.isEnabled) {
      if (item?.outOfStock) {
        return item?.hasValidModifiers ? '55%' : '65%';
      } else {
        return '100%';
      }
    } else {
      if (item?.outOfStock) {
        return '70%';
      } else {
        return '100%';
      }
    }
  };

  const isItemPriceEnabled = template?.itemPrice?.isEnabled;
  const fontFamily = fontFamilyProp || template?.fontFamilyName;

  // Generate item ID in the same format as Template1: item-{itemId}-category-{categoryId}
  const itemId = categoryId ? `item-${item.id}-category-${categoryId}` : undefined;

  return (
    <>
      {isDevelopment && (
        <BoxComponent
          id={itemId}
          key={item.id}
          sx={{
            overflow: 'hidden',
            width: '100%',
            ...(itemIndex === 0 ? {} : { ...getBottomMargin() }),
            ...(lastItemIndex === itemIndex && isItemPriceEnabled
              ? {
                  marginBottom: getScaleSize(normalizePixels(16, 'height'), scale),
                }
              : {}),
          }}
        >
          <BoxComponent
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: '100%',
              ...(isDescription ? { marginBottom: '0.5%' } : {}),
            }}
          >
            <BoxComponent
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: isItemPriceEnabled ? (item?.hasValidModifiers ? '63%' : '83%') : '100%',
                gap: `${getScaleSize(normalizePixels(10, 'height'), scale)} ${getScaleSize(
                  normalizePixels(10, 'width'),
                  scale
                )}`,
              }}
            >
              <TypographyComponent
                sx={{
                  color: template?.item?.itemName?.color ? template.item.itemName.color : '#FFFFFF',
                  fontWeight: template.item.itemName.fontWeight,
                  fontStyle: template.item.itemName.fontStyle,
                  letterSpacing: template.item.itemName.letterSpacing,
                  lineHeight: template.item.itemName.lineHeight,
                  minWidth: item?.outOfStock ? 'auto' : '100%',
                  maxWidth: getMaxWidth(),
                  fontFamily: fontFamily,
                  '-webkit-line-clamp': '1',
                  '-webkit-box-orient': 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: getScaleSize(normalizePixels(27 * Number(template?.item?.textScale ?? 1), 'height'), scale),
                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {item.itemName}
              </TypographyComponent>
              {item?.outOfStock && <OutOfStock fontFamily={fontFamily} />}
            </BoxComponent>

            {isItemPriceEnabled && item?.hasValidModifiers && (
              <BoxComponent sx={{ width: '35%' }}>
                {item.hasValidModifiers && (
                  <GridComponent container columnSpacing={1}>
                    {TEMPLATE_1_MODIFIERS.map((size) => (
                      <GridComponent key={size} item size={6}>
                        <TypographyComponent
                          sx={{
                            color: template?.item?.itemPrice?.color ? template.item?.itemPrice?.color : '#FFFEFE',
                            textAlign: 'center',
                            fontFamily: fontFamily,
                            lineHeight: template.item.itemPrice.lineHeight,
                            fontWeight: template.item.itemPrice.fontWeight,
                            fontStyle: template.item.itemPrice.fontStyle,
                            letterSpacing: template.item.itemPrice.letterSpacing,
                            fontSize: getScaleSize(
                              normalizePixels(27 * Number(template?.item?.textScale ?? 1), 'height'),
                              scale
                            ),
                            ...(item.outOfStock ? { opacity: '30%' } : {}),
                          }}
                        >
                          {item.modifiers && item?.modifiers[size]
                            ? process.env?.NX_NODE_ENV === APP_ENV.UAT && tenantId === 2971
                              ? Number(item?.modifiers[size])
                              : Number(item?.modifiers[size]).toFixed(2)
                            : '-'}
                        </TypographyComponent>
                      </GridComponent>
                    ))}
                  </GridComponent>
                )}
              </BoxComponent>
            )}

            {(!hasAnyModifier || !item.hasValidModifiers) && isItemPriceEnabled && (
              <TypographyComponent
                sx={{
                  color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#FFFEFE',
                  textAlign: 'center',
                  fontFamily: fontFamily,
                  lineHeight: template.item.itemPrice.lineHeight,
                  fontWeight: template.item.itemPrice.fontWeight,
                  fontStyle: template.item.itemPrice.fontStyle,
                  letterSpacing: template.item.itemPrice.letterSpacing,
                  fontSize: getScaleSize(normalizePixels(27 * Number(template?.item?.textScale ?? 1), 'height'), scale),
                  // width: '17%',
                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {Number(item.itemPrice).toFixed(2)}
              </TypographyComponent>
            )}
          </BoxComponent>

          {template?.itemDescription && item.itemDescription && template?.itemDescription.isEnabled && (
            <TypographyComponent
              sx={{
                fontWeight: template.item.itemDescription?.fontWeight,
                fontStyle: template.item.itemDescription?.fontStyle,
                letterSpacing: template.item.itemDescription?.letterSpacing,
                lineHeight: template.item.itemDescription?.lineHeight,
                color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#FFFFFF',
                width: isItemPriceEnabled ? (item?.hasValidModifiers ? '63%' : '83%') : '100%',
                fontFamily: fontFamily,
                display: '-webkit-box',
                '-webkit-line-clamp': '3',
                '-webkit-box-orient': 'vertical',
                overflow: 'hidden',
                fontSize: getScaleSize(normalizePixels(16 * Number(template?.item?.textScale ?? 1), 'height'), scale),
                ...(item.outOfStock ? { opacity: '30%' } : {}),
              }}
            >
              {item.itemDescription}
            </TypographyComponent>
          )}
        </BoxComponent>
      )}
    </>
  );
};

export default memo(Item);
